
import { format } from 'date-fns';

/**
 * Parses a datetime string from the database for display.
 * All poll and match dates (both scheduled and manually created) are stored as UTC
 * via .toISOString() before insertion. The database column is `timestamp without time zone`,
 * so Supabase returns the value without a Z suffix. We must always re-apply the Z
 * so JavaScript correctly treats the value as UTC and converts to the user's local timezone.
 */
const parseDateTimeString = (dateTimeString: string): Date => {
  // Check if the string already has a timezone indicator (Z, +HH:MM, -HH:MM)
  const hasTimezone = /[Zz]$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(dateTimeString.trim());

  if (hasTimezone) {
    return new Date(dateTimeString);
  }

  // No timezone indicator — the value came from a `timestamp without time zone` DB column
  // but was originally stored as UTC via .toISOString(). Append Z to restore UTC semantics
  // so the browser correctly converts it to local time for display.
  return new Date(dateTimeString + 'Z');
};

/**
 * Formats a datetime string from the database for display in the user's local timezone.
 * Handles both UTC timestamps (from scheduled polls) and local timestamps (from manual polls).
 */
export const formatPollDateTime = (dateTimeString: string, formatString: string = 'MMM dd • h:mm a'): string => {
  const localDate = parseDateTimeString(dateTimeString);
  return format(localDate, formatString);
};

/**
 * Formats a deadline with AM/PM included
 */
export const formatPollDeadline = (dateTimeString: string): string => {
  return formatPollDateTime(dateTimeString, 'MMM dd • h:mm a');
};

/**
 * Formats a proposed match date with a more detailed format including AM/PM.
 * Optionally converts to a specific IANA timezone (e.g. the poll's stored timezone)
 * so the displayed time matches the intended local time, not the browser's timezone.
 */
export const formatProposedDate = (dateTimeString: string, timezone?: string): string => {
  // Always parse as UTC (parseDateTimeString appends Z when needed)
  const date = parseDateTimeString(dateTimeString);
  if (isNaN(date.getTime())) return dateTimeString;
  if (timezone) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }
  return format(date, "PPP 'at' h:mm a");
};

/**
 * Returns the short timezone abbreviation for a given date and optional IANA timezone.
 * e.g. "CET", "CEST", "IST". Falls back to GMT offset format if no named abbreviation is available.
 * If `timezone` is provided, the abbreviation is derived from that timezone; otherwise the
 * browser's local timezone is used.
 * Passing the specific date ensures DST transitions are handled correctly
 * (e.g. a match in April shows "CEST" not "CET" for Europe/Madrid).
 */
export const getTimezoneAbbr = (forDate?: Date, timezone?: string): string => {
  const date = forDate ?? new Date();
  const tzOptions: Intl.DateTimeFormatOptions = { timeZoneName: 'short' };
  if (timezone) tzOptions.timeZone = timezone;

  // First try: short format (works on Safari and some Chrome builds)
  const short = new Intl.DateTimeFormat('en-US', tzOptions)
    .formatToParts(date)
    .find(p => p.type === 'timeZoneName')?.value ?? '';

  // If we got a proper named abbreviation (e.g. "CEST", "IST"), use it directly.
  // Chromium-based browsers often return "GMT+2" instead of "CEST" — detect that.
  if (short && !/^(GMT|UTC)/.test(short)) return short;

  // Fallback: derive abbreviation from the long timezone name.
  // e.g. "Central European Summer Time" → "CEST"
  const longTzOptions: Intl.DateTimeFormatOptions = { timeZoneName: 'long' };
  if (timezone) longTzOptions.timeZone = timezone;
  const long = new Intl.DateTimeFormat('en-US', longTzOptions)
    .formatToParts(date)
    .find(p => p.type === 'timeZoneName')?.value ?? '';

  if (long && !long.startsWith('GMT') && !long.startsWith('UTC')) {
    const abbr = long.split(' ').map(w => w[0]).join('').toUpperCase();
    if (abbr.length >= 2 && abbr.length <= 5) return abbr;
  }

  // Last resort: return the GMT offset string as-is
  return short;
};

/**
 * Converts a Date to the "YYYY-MM-DDTHH:mm" format required by <input type="datetime-local">.
 * Uses local time components so the `min` attribute and default values reflect the
 * user's local timezone rather than UTC.
 */
export const toLocalDateTimeInput = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Export the parser for use in other components (e.g., ScheduledMatchCard)
 */
export { parseDateTimeString };
